import type { WhatsNewAnnouncement } from 'src/services/api/whatsNewApi';
import { DateUtils } from 'src/utils';

// Keeps the modal out of the way while a new workspace is still onboarding.
export const MIN_WORKSPACE_AGE_HOURS = 3 * 24;

interface SelectWhatsNewAnnouncementInput {
  announcements: WhatsNewAnnouncement[];
  isAdmin: boolean;
}

/**
 * The single announcement the user is eligible for: the most recent one,
 * skipping admin-only entries for non-admins so they fall through to the
 * latest announcement meant for everyone.
 */
export const selectWhatsNewAnnouncement = ({
  announcements,
  isAdmin,
}: SelectWhatsNewAnnouncementInput): WhatsNewAnnouncement | undefined => {
  const eligible = announcements.filter(
    (announcement) => isAdmin || !announcement.adminsOnly,
  );
  // publishDate is a calendar day, so break ties by id: every tab must pick
  // the same announcement or same-day pairs would alternate on dismissal.
  return [...eligible]
    .sort(
      (a, b) =>
        Date.parse(b.publishDate) - Date.parse(a.publishDate) ||
        a.id.localeCompare(b.id),
    )
    .at(0);
};

interface ShouldShowWhatsNewInput {
  announcementId: string;
  publishDate: string;
  dismissedId: string | undefined;
  /** Publish time (epoch ms) of the newest announcement the user dismissed. */
  dismissedAt: number | undefined;
  workspaceCreatedAt: Date | undefined;
  isForcedOpen: boolean;
}

/**
 * Whether the selected announcement should open: nothing as new has been
 * dismissed and the workspace is past onboarding. Dismissing an announcement
 * dismisses everything published before it, so an older one never resurfaces
 * after a newer one is closed. A forced open bypasses both checks.
 */
export const shouldShowWhatsNew = ({
  announcementId,
  publishDate,
  dismissedId,
  dismissedAt,
  workspaceCreatedAt,
  isForcedOpen,
}: ShouldShowWhatsNewInput): boolean => {
  if (isForcedOpen) return true;
  // Dismissals saved before the publish time was stored only carry the id.
  if (dismissedId === announcementId) return false;
  // Strictly older only: publishDate is a calendar day, so a second
  // announcement published the same day must still show.
  if (dismissedAt !== undefined && Date.parse(publishDate) < dismissedAt) {
    return false;
  }
  return (
    !!workspaceCreatedAt &&
    DateUtils.getDiffFromDate(new Date(workspaceCreatedAt), 'hours') >=
      MIN_WORKSPACE_AGE_HOURS
  );
};
